"use client";

import React, { useEffect, useState } from "react";
import { ChevronDown, Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu, type MenuItem as MenuItemType } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function RecursiveDesktopSubmenu({
  items
}: {
  items: MenuItemType[];
}) {
  const hasSubgroups = items.some(item => item.children && item.children.length > 0);
  if (hasSubgroups) {
    const groupItems = items.filter(item => item.children && item.children.length > 0);
    const standaloneItems = items.filter(item => !item.children || item.children.length === 0);
    return <div className="w-[740px] max-w-[92vw] p-5 bg-popover text-popover-foreground rounded-md border border-border shadow-md">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-6">
          {groupItems.map((group, i) => <div key={group.id || i} data-index={i} className="flex flex-col space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-foreground pb-1 border-b border-border/80 select-none">
                {group.label}
              </span>
              <ul className="space-y-1">
                {group.children?.map((subItem, j) => <li key={subItem.id || j} data-index={j}>
                    <NavigationMenuLink asChild>
                      <Link to={subItem.href || "#"} className="block py-1 text-xs text-muted-foreground hover:text-primary transition-colors focus:text-primary">
                        {subItem.label}
                      </Link>
                    </NavigationMenuLink>
                  </li>)}
              </ul>
            </div>)}
        </div>
        {standaloneItems.length > 0 && <div className="mt-4 pt-3 border-t border-border flex flex-wrap items-center justify-between gap-2">
            {standaloneItems.map((item, i) => <NavigationMenuLink key={item.id || i} data-index={i} asChild>
                <Link to={item.href || "#"} className="text-xs font-semibold text-primary hover:underline transition-colors">
                  {item.label} &rarr;
                </Link>
              </NavigationMenuLink>)}
          </div>}
      </div>;
  }
  return <ul className="grid w-60 gap-1 p-2 bg-popover text-popover-foreground rounded-md border border-border shadow-md">
      {items.map((child, i) => <li key={child.id || i} data-index={i} className="relative group/sub">
          {child.children && child.children.length > 0 ? <div className="flex flex-col">
              <span className="px-3 py-1.5 text-xs font-bold text-foreground select-none">
                {child.label}
              </span>
              <div className="pl-2">
                <RecursiveDesktopSubmenu items={child.children} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={child.href || "#"} className="block px-3 py-2 text-sm font-medium rounded-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-colors">
                {child.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-28 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-0.5 xl:gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id || i} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent px-2.5 xl:px-3.5 whitespace-nowrap text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <RecursiveDesktopSubmenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex h-9 items-center justify-center rounded-md px-2.5 xl:px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground whitespace-nowrap">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function RecursiveMobileList({
  items,
  depth = 0,
  onItemClick
}: {
  items: MenuItemType[];
  depth?: number;
  onItemClick: () => void;
}) {
  return <ul className={depth > 0 ? "pl-3 border-l border-border mt-1 space-y-1" : "space-y-1"}>
      {items.map((item, i) => <li key={item.id || i} data-index={i}>
          <Link to={item.href || "#"} onClick={onItemClick} className="flex items-center justify-between py-2 px-2 text-sm font-medium rounded-md text-foreground hover:bg-accent hover:text-accent-foreground transition-colors">
            <span>{item.label}</span>
          </Link>
          {item.children && item.children.length > 0 && <RecursiveMobileList items={item.children} depth={depth + 1} onItemClick={onItemClick} />}
        </li>)}
    </ul>;
}
function MobileNavConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3 p-2">
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-5/6 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-2/3 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <RecursiveMobileList items={menuItems} onItemClick={onClose} />;
}
export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="light" className={`sticky top-0 z-50 w-full transition-all duration-200 ${isScrolled ? "bg-background/95 backdrop-blur-md border-b border-border shadow-xs" : "bg-background border-b border-border/60"}`}>
      <header className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between gap-4">
          <div className="flex items-center shrink-0">
            <Link to="/" className="flex items-center focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring rounded-md" aria-label="BMF Scaffolding Ltd homepage">
              <WvcLogo className="h-11 md:h-14 w-auto" />
            </Link>
          </div>

          <div className="flex items-center justify-center flex-1">
            <MenuProvider menu_id="16">
              <DesktopNavConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <a href="tel:01344929900" className="group hidden xl:inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary focus-visible:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring transition-colors px-3 py-2 rounded-md">
              <span className="flex size-8 items-center justify-center rounded-lg bg-muted text-foreground group-hover:bg-primary/10 group-hover:text-primary group-focus-visible:bg-primary/10 group-focus-visible:text-primary transition-colors">
                <Phone className="size-4" />
              </span>
              <span className="font-medium text-foreground group-hover:text-primary group-focus-visible:text-primary transition-colors">
                Call 01344 929900
              </span>
            </a>

            <Button asChild size="default" className="font-semibold shadow-xs">
              <Link to="/ContactRequestQuotePage">Request a quote</Link>
            </Button>

            <div className="lg:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="border-border text-foreground" aria-label="Open navigation menu">
                    <Menu className="size-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full max-w-xs sm:max-w-sm flex flex-col justify-between p-6 bg-background">
                  <div>
                    <SheetHeader className="p-0 pb-4 border-b border-border text-left">
                      <SheetTitle className="text-base font-bold text-foreground">
                        Navigation
                      </SheetTitle>
                      <SheetDescription className="text-xs text-muted-foreground">
                        BMF Scaffolding Ltd across the South East
                      </SheetDescription>
                    </SheetHeader>

                    <div className="py-4 max-h-[calc(100vh-250px)] overflow-y-auto">
                      <MenuProvider menu_id="16">
                        <MobileNavConsumer onClose={() => setMobileOpen(false)} />
                      </MenuProvider>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border flex flex-col gap-3">
                    <Button asChild className="w-full font-semibold">
                      <Link to="/ContactRequestQuotePage" onClick={() => setMobileOpen(false)}>
                        Request a quote
                      </Link>
                    </Button>
                    <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 text-sm font-medium py-2.5 px-4 rounded-md border border-border bg-background text-foreground hover:bg-muted hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring transition-colors shadow-xs">
                      <Phone className="size-4 text-primary shrink-0" />
                      <span className="font-semibold text-foreground">Call our team</span>
                    </a>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </section>;
}